import {useCallback, useEffect, useState} from 'react';

// Threshold for showing specific time vs generic "Processing" message
const DISPLAY_TIME_THRESHOLD_MINUTES = 60; // 60 minutes

/**
 * Returns:
 * - remainingMinutes: estimated minutes until completion (null if invalid or over threshold)
 * - progressPercentage: 0-100 progress (null if invalid or time exceeded)
 * - hasExceededEstimate: true if current time has exceeded the estimated completion time, false otherwise
 * If remaining time is over 60 minutes, returns null to trigger generic "Processing" display.
 */
function useLiveEstimatedMinutes(
  estimatedCompletionTime?: string | null,
  createdAt?: string | Date | null,
  updateInterval = 1000 // default to 1 seconds
): {
  remainingMinutes: number | null;
  progressPercentage: number | null;
  hasExceededEstimate: boolean;
} {
  const calculate = useCallback(() => {
    if (!estimatedCompletionTime || !createdAt) {
      return {
        remainingMinutes: null,
        progressPercentage: null,
        hasExceededEstimate: false,
      };
    }
    const now = Date.now();
    const end = new Date(estimatedCompletionTime).getTime();
    const start = new Date(createdAt).getTime();
    if (Number.isNaN(end) || Number.isNaN(start) || end <= start) {
      return {
        remainingMinutes: null,
        progressPercentage: null,
        hasExceededEstimate: false,
      };
    }
    if (now >= end) {
      return {
        remainingMinutes: null,
        progressPercentage: null,
        hasExceededEstimate: true,
      };
    }
    const remainingMinutes = Math.max(Math.round((end - now) / 60000), 0);
    const totalDuration = end - start;
    const elapsed = now - start;
    // cap the progress percentage to 99% to avoid showing 100% before completion
    const progressPercentage = Math.min(
      Math.max((elapsed / totalDuration) * 100, 0),
      99
    );

    // If remaining time is over threshold, return null for remainingMinutes
    // to trigger generic "Processing" display in UI components
    const displayMinutes =
      remainingMinutes > DISPLAY_TIME_THRESHOLD_MINUTES
        ? null
        : remainingMinutes;

    return {
      remainingMinutes: displayMinutes,
      progressPercentage,
      hasExceededEstimate: false,
    };
  }, [createdAt, estimatedCompletionTime]);

  const [
    {remainingMinutes, progressPercentage, hasExceededEstimate},
    setState,
  ] = useState(calculate());

  useEffect(() => {
    const update = () => {
      setState(calculate());
    };
    update();
    if (!estimatedCompletionTime || !createdAt) {
      return () => {};
    }
    const end = new Date(estimatedCompletionTime).getTime();
    if (Number.isNaN(end)) {
      return () => {};
    }
    const interval = setInterval(update, updateInterval);
    return () => {
      clearInterval(interval);
    };
  }, [estimatedCompletionTime, createdAt, updateInterval, calculate]);

  return {remainingMinutes, progressPercentage, hasExceededEstimate};
}

export default useLiveEstimatedMinutes;
